/*
    Petrophysics Log Viewer — professional display defaults.

    Source: docs/domain/log_viewer_defaults.md (petroleum agent)
    These are *default templates*, NOT hard rules — every value is overridable
    per user/asset via Redux settings.curveOverrides.

    High-risk items (per petroleum agent), keep intact:
      1. Density-Neutron compatible scale ratio 1:0.3 (RHOB 1.95-2.95 / NPHI 0.45..-0.15)
         — changing it breaks the gas crossover indication.
      2. Reverse curves (NPHI / DT / DTS / PHIE / SW): high value on the left.
      3. Mnemonic alias mapping — without it the LAS mnemonics won't match the
         template and the whole default set is wasted.
*/

// canonical mnemonic -> default display config
export const CURVE_DEFAULTS = {
    GR:   { color: "#008000", style: "solid",   width: 1, min: 0,     max: 150,   unit: "gAPI",  scale: "linear", reverse: false, track: "t1" },
    SP:   { color: "#000000", style: "solid",   width: 1, min: -160,  max: 40,    unit: "mV",    scale: "linear", reverse: false, track: "t1" },
    CALI: { color: "#666666", style: "dashed",  width: 1, min: 6,     max: 16,    unit: "in",    scale: "linear", reverse: false, track: "t1" },
    BS:   { color: "#000000", style: "dashdot", width: 1, min: 6,     max: 16,    unit: "in",    scale: "linear", reverse: false, track: "t1" },
    RT:   { color: "#FF0000", style: "solid",   width: 1, min: 0.2,   max: 2000,  unit: "ohm.m", scale: "log",    reverse: false, track: "t2" },
    RM:   { color: "#808000", style: "dashed",  width: 1, min: 0.2,   max: 2000,  unit: "ohm.m", scale: "log",    reverse: false, track: "t2" },
    RS:   { color: "#0000FF", style: "dashed",  width: 1, min: 0.2,   max: 2000,  unit: "ohm.m", scale: "log",    reverse: false, track: "t2" },
    RXO:  { color: "#800080", style: "dotted",  width: 1, min: 0.2,   max: 2000,  unit: "ohm.m", scale: "log",    reverse: false, track: "t2" },
    RHOB: { color: "#FF0000", style: "solid",   width: 1, min: 1.95,  max: 2.95,  unit: "g/cm3", scale: "linear", reverse: false, track: "t3" },
    NPHI: { color: "#0000FF", style: "dashed",  width: 1, min: 0.45,  max: -0.15, unit: "v/v",   scale: "linear", reverse: true,  track: "t3" },
    DT:   { color: "#0070C0", style: "solid",   width: 1, min: 140,   max: 40,    unit: "us/ft", scale: "linear", reverse: true,  track: "t3" },
    DTS:  { color: "#00B0F0", style: "dashed",  width: 1, min: 240,   max: 40,    unit: "us/ft", scale: "linear", reverse: true,  track: "t3" },
    PEF:  { color: "#FF00FF", style: "dashed",  width: 1, min: 0,     max: 10,    unit: "b/e",   scale: "linear", reverse: false, track: "t3" },
    DRHO: { color: "#000000", style: "dashed",  width: 1, min: -0.25, max: 0.25,  unit: "g/cm3", scale: "linear", reverse: false, track: "t3" },
    PERM: { color: "#FF7F00", style: "solid",   width: 1, min: 0.01,  max: 10000, unit: "mD",    scale: "log",    reverse: false, track: "calc" },
    PHIE: { color: "#000080", style: "solid",   width: 1, min: 0.5,   max: 0,     unit: "v/v",   scale: "linear", reverse: true,  track: "calc" },
    SW:   { color: "#0000FF", style: "solid",   width: 1, min: 1,     max: 0,     unit: "v/v",   scale: "linear", reverse: true,  track: "calc" },
    VSH:  { color: "#808000", style: "solid",   width: 1, min: 0,     max: 1,     unit: "v/v",   scale: "linear", reverse: false, track: "calc" },
};

// alias -> canonical mnemonic. LAS mnemonics vary by vendor/tool; map them back.
export const CURVE_ALIAS = {
    GR:   ["GR", "GRD", "SGR", "CGR", "GAMM", "GRGC", "GR_EDTC"],
    SP:   ["SP", "SPONTANEOUS"],
    CALI: ["CALI", "CAL", "CALS", "HCAL", "CALX", "CALY"],
    BS:   ["BS", "BIT", "BITSIZE"],
    RT:   ["RT", "RD", "LLD", "ILD", "RT_HRLT", "AT90", "RLA5", "M2R9"],
    RM:   ["RM", "LLS", "ILM", "AT30", "RLA3", "M2R3"],
    RS:   ["RS", "SFL", "AT10", "RLA1", "M2R1"],
    RXO:  ["RXO", "MSFL", "RXOZ", "RXO8"],
    RHOB: ["RHOB", "DEN", "ZDEN", "RHOZ", "RHOM"],
    NPHI: ["NPHI", "NEU", "TNPH", "CNL", "NPOR", "NPRL", "APLC"],
    DT:   ["DT", "AC", "DTCO", "DT24", "DTP", "DTLN"],
    DTS:  ["DTS", "DTSM", "DTSH"],
    PEF:  ["PEF", "PE", "PEFZ"],
    DRHO: ["DRHO", "HDRA", "DCOR", "ZCOR"],
    PERM: ["PERM", "K", "KLOGH", "KINT"],
    PHIE: ["PHIE", "PHIT", "PORE"],
    SW:   ["SW", "SWE", "SWT", "SWIRR"],
    VSH:  ["VSH", "VCL", "VSHALE", "VCLAY"],
};

// reverse lookup: any alias (upper) -> canonical mnemonic
const ALIAS_TO_CANONICAL = (() => {
    const map = {};
    Object.keys(CURVE_ALIAS).forEach((canonical) => {
        CURVE_ALIAS[canonical].forEach((alias) => {
            map[alias.toUpperCase()] = canonical;
        });
    });
    return map;
})();

// classic 3/4-track layout
export const TRACK_LAYOUT = [
    { id: "t1", scale: "linear", label: "GR / SP / CALI" },
    { id: "depth", type: "depth" },
    { id: "t2", scale: "log", label: "Resistivity" },
    { id: "t3", scale: "linear", label: "Density / Neutron" },
    { id: "calc", scale: "linear", label: "Computed" },
];

export const FILLS = {
    // RHOB 1.95-2.95 vs NPHI 0.45..-0.15 — ratio 1:0.3 is the limestone-compatible
    // scale; gas shows as crossover. DO NOT change the ratio.
    densityNeutronGas: { enabled: true, between: ["RHOB", "NPHI"], colorCrossover: "#FFFF00", colorShale: "#808080" },
    grCutoff: { enabled: false, curve: "GR", cutoff: 75, sandColor: "#FFFF00", shaleColor: "#7F7F00" },
};

export const DEPTH_GRID = {
    field:  { major: 100, minor: 50, gridline: 10, scales: ["1:240", "1:600"] },
    metric: { major: 100, minor: 25, gridline: 10, scales: ["1:200", "1:500", "1:1000"] },
};

export const TOPS_DEFAULTS = {
    lineStyle: "solid",
    lineWidth: 1,
    fullWidth: true,
    labelPosition: "above-left",
    unconformityColor: "#FF0000",
};

// stable fallback palette for unknown mnemonics — deterministic by hash, so the
// same curve gets the same color across reloads (replaces randomcolor).
const FALLBACK_PALETTE = [
    "#5470c6", "#91cc75", "#fac858", "#ee6666", "#73c0de",
    "#3ba272", "#fc8452", "#9a60b4", "#ea7ccc", "#c0504d",
    "#1f77b4", "#2ca02c", "#9467bd", "#8c564b", "#e377c2",
];

const hashString = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
        h = (h * 31 + str.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
};

export const fallbackColor = (logName) =>
    FALLBACK_PALETTE[hashString(String(logName || "")) % FALLBACK_PALETTE.length];

/**
 * Resolve a raw LAS mnemonic to its canonical form.
 * Strips common suffixes (e.g. "GR_1", "RHOB:RHOZ") and matches the alias table.
 * @param {string} logName raw mnemonic from the log
 * @returns {string|null} canonical mnemonic (e.g. "RT") or null if unknown
 */
export const resolveMnemonic = (logName) => {
    if (!logName) return null;
    const raw = String(logName).toUpperCase().trim();
    if (ALIAS_TO_CANONICAL[raw]) return ALIAS_TO_CANONICAL[raw];

    // strip a trailing numeric/qualifier suffix: GR_1, GR.1, GR-2, GRX -> try GR
    const base = raw.replace(/[\s._-]?\d+$/, "").replace(/[\s._-].*$/, "");
    if (ALIAS_TO_CANONICAL[base]) return ALIAS_TO_CANONICAL[base];

    // prefix match against known canonicals (e.g. "RHOBC" -> "RHOB")
    const canonicals = Object.keys(CURVE_DEFAULTS);
    const hit = canonicals.find((c) => raw.startsWith(c));
    return hit || null;
};

/**
 * Get the merged display config for a curve: template default overlaid with any
 * user override (keyed by canonical mnemonic). Always returns a usable object.
 * @param {string} logName raw mnemonic
 * @param {object} overrides Redux settings.curveOverrides keyed by canonical mnemonic
 * @returns {object} { color, style, width, min, max, unit, scale, reverse, track, mnemonic }
 */
export const getCurveDefault = (logName, overrides = {}) => {
    const mnemonic = resolveMnemonic(logName);
    const base = (mnemonic && CURVE_DEFAULTS[mnemonic]) || {
        color: fallbackColor(logName),
        style: "solid",
        width: 1,
        min: undefined,
        max: undefined,
        unit: "",
        scale: "linear",
        reverse: false,
        track: "calc",
    };
    const override = (mnemonic && overrides && overrides[mnemonic]) || {};
    return { ...base, ...override, mnemonic: mnemonic || logName };
};

// echarts line dash patterns
export const lineDash = (style) => {
    switch (style) {
        case "dashed":  return [8, 4];
        case "dotted":  return [2, 3];
        case "dashdot": return [8, 4, 2, 4];
        default:        return undefined; // solid
    }
};

export const LINE_STYLE_OPTIONS = [
    { label: "实线 Solid", value: "solid" },
    { label: "虚线 Dashed", value: "dashed" },
    { label: "点线 Dotted", value: "dotted" },
    { label: "点划线 Dash-dot", value: "dashdot" },
];
